import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { apiHelpers, formatDate } from '../utils/api';
import {
  decideUpdatePrompt,
  dismissUpdateForThisLaunch,
  formatDownloadProgress,
  normalisePlatform,
  wasUpdateDismissedThisLaunch,
} from '../utils/desktopUpdate';

/**
 * WP-UPDATE — "the desktop app has been updated, here is what to do".
 *
 * The desktop app is a thin Electron shell around the live site. A NAS deploy
 * reaches everybody instantly, but a new shell has to be installed by hand, and
 * nothing used to tell anyone that a new one existed. This banner does, in the
 * app itself, with the exact steps.
 *
 * It renders ONLY inside the Electron shell (`window.electron`). In a browser
 * there is nothing to reinstall, so a person in Chrome never sees it.
 *
 * `/api/desktop-client/info` is public, so this works on the login screen too —
 * which is where a person sits when they first open the app, and the best
 * moment to be told to quit and reinstall.
 *
 * "Later" closes it for this launch only. Nothing is written down, so the
 * banner is back next time the app is opened. That is deliberate: three people
 * share this app and an update that can be permanently dismissed never happens.
 * The flag lives in utils/desktopUpdate (a module, loaded once per launch)
 * rather than in this component's state, because every page renders its own
 * <Layout> — so changing page would otherwise bring the banner straight back.
 */

// True only inside the Electron shell.
const inDesktopShell = () => typeof window !== 'undefined' && !!window.electron;

// The backend already hands back DD/MM/YYYY (it formats the installer's file
// date), so passing it through formatDate would turn it into an empty string —
// formatDate parses dates, it does not re-read ones already in house shape.
// Anything else (an ISO date, say) goes through formatDate as usual.
const displayReleaseDate = (value) => {
  if (!value) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(String(value))) return String(value);
  return formatDate(value);
};

const DesktopUpdateBanner = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);       // the platform's block from /info
  const [minimumVersion, setMinimumVersion] = useState(null);
  const [appVersion, setAppVersion] = useState(null);
  const [platform, setPlatform] = useState('mac');
  const [checked, setChecked] = useState(false);
  const [dismissed, setDismissed] = useState(wasUpdateDismissedThisLaunch);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(null); // { received_bytes, total_bytes }
  const [done, setDone] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!inDesktopShell()) { setChecked(true); return; }
    let cancelled = false;

    const load = async () => {
      // Which OS, and which build is this shell? An old 1.0.0 shell has no
      // getAppVersion at all — that is the signal that it is out of date.
      let plat = 'mac';
      try {
        const raw = typeof window.electron.getPlatform === 'function'
          ? await window.electron.getPlatform()
          : window.electron.platform;
        plat = normalisePlatform(raw);
      } catch (e) { plat = 'mac'; }

      let version = null;
      try {
        if (typeof window.electron.getAppVersion === 'function') {
          version = await window.electron.getAppVersion();
        }
      } catch (e) { version = null; }

      let payload = null;
      try {
        const resp = await apiHelpers.getDesktopClientInfo();
        payload = resp?.data || null;
      } catch (e) {
        // Offline, or the endpoint is down. Say nothing rather than guess.
        payload = null;
      }

      if (cancelled || !mounted.current) return;
      setPlatform(plat);
      setAppVersion(version || null);
      setInfo(payload?.[plat]?.available ? payload[plat] : null);
      setMinimumVersion(payload?.minimum_version || null);
      setChecked(true);
    };

    load();
    return () => { cancelled = true; };
  }, []);

  // Progress from the shell while the installer downloads.
  useEffect(() => {
    if (!inDesktopShell() || typeof window.electron.onDesktopUpdateProgress !== 'function') return undefined;
    const off = window.electron.onDesktopUpdateProgress((data) => {
      if (mounted.current) setProgress(data || null);
    });
    return typeof off === 'function' ? off : undefined;
  }, []);

  const downloadUrl = useCallback(() => {
    // The shell needs a full address: it hands this to Electron's downloader,
    // which has no page to resolve a relative path against.
    const base = process.env.REACT_APP_BACKEND_URL
      || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}/api/desktop-client/download/${platform}`;
  }, [platform]);

  const hideForThisLaunch = useCallback(() => {
    dismissUpdateForThisLaunch();
    setDismissed(true);
  }, []);

  const goToStaffSecurity = useCallback(() => {
    setDismissed(true);
    navigate('/staff-security');
  }, [navigate]);

  const canDownloadInShell = inDesktopShell()
    && typeof window.electron.downloadDesktopUpdate === 'function';

  const handleUpdateNow = async () => {
    if (!canDownloadInShell) { goToStaffSecurity(); return; }
    setErrorMessage('');
    setDownloading(true);
    setProgress(null);
    try {
      const result = await window.electron.downloadDesktopUpdate(downloadUrl(), info?.filename || '');
      if (!mounted.current) return;
      if (result && result.ok && result.opened !== false) {
        setDone(true);
      } else if (result && result.ok) {
        // Downloaded, but the Mac would not open it by itself. Say so rather
        // than claim the installer is on screen when it is not.
        setErrorMessage(result.message
          || 'The installer downloaded to your Downloads folder but would not open by itself. Open it from Downloads.');
      } else {
        setErrorMessage((result && result.message) || 'The download did not finish. Please try again.');
      }
    } catch (e) {
      if (mounted.current) setErrorMessage('The download could not be started. Please try again.');
    } finally {
      if (mounted.current) setDownloading(false);
    }
  };

  if (!inDesktopShell() || !checked || dismissed) return null;

  const verdict = decideUpdatePrompt({
    appVersion,
    serverVersion: info?.version || null,
    minimumVersion,
    platform,
  });
  if (!verdict.show) return null;

  const serverVersion = info?.version || '';
  const sizeMb = info?.size_mb;
  const releasedOn = displayReleaseDate(info?.released_on);
  const noteLines = String(info?.notes || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const heading = verdict.required
    ? 'This update is required before you can keep working'
    : `Desktop app updated on ${releasedOn || 'a newer date'} (version ${serverVersion})`;

  const youHave = appVersion ? `version ${appVersion}` : 'an older version';

  const stepOne = canDownloadInShell
    ? `Click Update now. The installer downloads to your Downloads folder (about ${sizeMb} MB; a minute or two on office Wi-Fi).`
    : 'Click Update now. It opens Staff & Security. Press Download Desktop App there.';

  return (
    <div
      className="bg-amber-500/10 border-b border-amber-500/40 px-4 py-3"
      role="region"
      aria-label="Desktop app update"
      data-testid="desktop-update-banner"
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon
            className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5"
            title="The desktop app on this computer is out of date"
            aria-label="The desktop app on this computer is out of date"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-amber-200">{heading}</h2>
              {serverVersion && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-200 border border-amber-500/40">
                  v{serverVersion}
                </span>
              )}
            </div>
            <p className="text-xs text-amber-200/80 mt-1">You have {youHave}.</p>

            {noteLines.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-amber-100/90 list-disc list-inside">
                {noteLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}

            <div className="mt-3">
              <div className="text-xs font-semibold text-amber-200">What you need to do</div>
              <ol className="mt-1 space-y-1 text-xs text-amber-100/90 list-decimal list-inside">
                <li>{stepOne}</li>
                <li>When the installer window opens, drag Misty Manufacturing onto the Applications folder and choose Replace.</li>
                <li>Quit this app (Cmd+Q) and open Misty Manufacturing from Applications. The first time, right-click it and choose Open.</li>
                <li>Your login and printers are kept.</li>
              </ol>
            </div>

            {downloading && (
              <div className="mt-3" data-testid="desktop-update-progress">
                <div className="h-2 w-full bg-gray-900/60 rounded-full overflow-hidden border border-amber-500/30">
                  <div
                    className="h-full bg-amber-400 transition-all duration-200"
                    style={{ width: `${progress?.percent || 0}%` }}
                  />
                </div>
                <p className="text-xs text-amber-200/80 mt-1">
                  Downloading… {formatDownloadProgress(progress?.received_bytes, progress?.total_bytes)}
                </p>
              </div>
            )}

            {done && (
              <p className="text-xs text-green-300 mt-3" data-testid="desktop-update-done">
                Installer opened. Follow steps 2 and 3.
              </p>
            )}

            {errorMessage && (
              <div className="mt-3">
                <p className="text-xs text-red-300">{errorMessage}</p>
                <button
                  type="button"
                  onClick={goToStaffSecurity}
                  className="text-xs text-amber-300 underline hover:text-amber-200 mt-1"
                  title="Open Staff & Security, where the desktop app can be downloaded by hand"
                  aria-label="Open Staff & Security, where the desktop app can be downloaded by hand"
                >
                  Download it from Staff &amp; Security instead
                </button>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleUpdateNow}
                disabled={downloading}
                className="misty-button misty-button-primary text-sm inline-flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                title={downloading
                  ? 'The installer is downloading — please wait'
                  : 'Download the new desktop app and open the installer'}
                aria-label={downloading
                  ? 'The installer is downloading — please wait'
                  : 'Download the new desktop app and open the installer'}
                data-testid="desktop-update-now"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                Update now
              </button>
              {!verdict.required && (
                <button
                  type="button"
                  onClick={hideForThisLaunch}
                  className="misty-button misty-button-secondary text-sm inline-flex items-center"
                  title="Hide this until the next time the app is opened"
                  aria-label="Hide this until the next time the app is opened"
                  data-testid="desktop-update-later"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                  Later
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopUpdateBanner;
